import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, ChevronDown, User, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import OptimizedImage from '@/components/OptimizedImage';
import systemiqueLogo from '@/assets/systemique.png';
import systemiqueLogoWebp from '@/assets/systemique.webp';

// Navigation items - defined outside component for better performance
const navigation = [
  { 
    name: 'Home', 
    href: '/'
  },
  { 
    name: 'Over mij', 
    href: '/over'
  },
  { 
    name: 'Aanbod', 
    href: '/aanbod',
    subItems: [
      { name: 'Organisatieopstellingen', href: '/organisatieopstellingen' },
      { name: 'Opleidingstrajecten', href: '/opleidingstrajecten' },
      { name: '1-op-1 opstelling/coaching', href: '/1-op-1' },
      { name: 'Familieopstellingen', href: '/familieopstellingen' },
      { name: 'Teamcoaching', href: '/teamcoaching' },
    ]
  },
  { 
    name: 'Agenda', 
    href: '/agenda',
    subItems: [
      { name: 'Themadag Burn-out', href: '/themadag/burn-out-organisaties' },
      { name: 'Leergang systemisch leiderschap', href: '/leergang-systemisch-leiderschap' },
    ]
  },
  {
    name: 'Contact',
    href: '/contact',
  },
];

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [mobileDropdown, setMobileDropdown] = useState<string | null>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isAdmin, signOut } = useAuth();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Lock body scroll when mobile menu is open and close on Escape
  useEffect(() => {
    const body = document.body;
    if (isOpen) {
      body.style.overflow = 'hidden';
    } else {
      body.style.overflow = '';
    }

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      body.style.overflow = '';
    };
  }, [isOpen]);

  // Handle hash navigation on page load and location change
  useEffect(() => {
    const hash = location.hash.replace('#', '');
    if (hash) {
      setTimeout(() => {
        const element = document.getElementById(hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  }, [location]);

  const isActive = (href: string) => location.pathname === href;

  const handleHashNavigation = (href: string) => {
    const [path, hash] = href.split('#');
    
    // If we're navigating to the same page, just scroll to the hash
    if (path === location.pathname || (path === '/' && location.pathname === '/')) {
      if (hash) {
        setTimeout(() => {
          const element = document.getElementById(hash);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 100);
      }
    }
    
    setActiveDropdown(null);
  };

  const handleLogout = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Error during sign out:', error);
    } finally {
      setIsOpen(false);
      setMobileDropdown(null);
      navigate('/');
    }
  };

  return (
    <nav className={`fixed top-0 w-full z-50 transition-all duration-500 ${
      isScrolled ? 'bg-[#FAEBEE]/90 backdrop-blur-md py-3 shadow-sm' : 'bg-transparent py-6'
    }`}>
      <div className="container mx-auto px-6">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center hover-lift relative z-50">
            <OptimizedImage src={systemiqueLogo} webpSrc={systemiqueLogoWebp} alt="Systemique - Systemisch werk en coaching Castricum" className="h-8 w-auto object-contain" width={120} height={32} priority={true} />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navigation.map((item) => (
              <div 
                key={item.name}
                className="relative group h-full"
                onMouseEnter={() => {
                  if (item.subItems) setActiveDropdown(item.name);
                }}
                onMouseLeave={() => {
                  setTimeout(() => setActiveDropdown(null), 200);
                }}
              >
                <Link
                  to={item.href}
                  className={`text-sm font-medium uppercase tracking-wider flex items-center gap-1 hover:text-[#854C73] transition-colors ${
                    isActive(item.href) ? 'text-[#854C73] font-bold' : 'text-[#6b4912]'
                  }`}
                >
                  {item.name}
                  {item.subItems && (
                    <ChevronDown 
                      size={14}
                      className={`transition-all duration-200 ${
                        activeDropdown === item.name ? 'rotate-180' : ''
                      }`}
                    />
                  )}
                </Link>
                
                {/* Dropdown Menu */}
                {activeDropdown === item.name && item.subItems && (
                  <div className={`absolute top-full left-1/2 -translate-x-1/2 pt-4 w-64 transition-all duration-300 ${
                    activeDropdown === item.name ? 'opacity-100 translate-y-0 visible' : 'opacity-0 translate-y-2 invisible'
                  }`}>
                    <div className="bg-white rounded-xl shadow-xl border border-[#FAEBEE] overflow-hidden p-2">
                      {item.subItems.map((subItem) => (
                        <Link
                          key={subItem.name}
                          to={subItem.href}
                          className="block w-full text-left px-4 py-2 text-sm text-[#6b4912] hover:bg-[#FAEBEE] hover:text-[#854C73] rounded-lg transition-colors"
                          onClick={() => handleHashNavigation(subItem.href)}
                        >
                          {subItem.name}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
            <Link to="/contact">
              <Button 
                variant="default" 
                size="sm" 
                className="ml-4 rounded-full bg-[#854C73] text-[#F9EBEF] hover:bg-[#590D0D] hover:scale-105 transition-all"
              >
                Contact
              </Button>
            </Link>
            {user ? (
              <>
                {isAdmin && (
                  <Link to="/admin">
                    <Button variant="outline" size="sm" className="ml-2">
                      <Shield className="h-4 w-4 mr-2" />
                      Admin
                    </Button>
                  </Link>
                )}
                <Link to="/mijn-strippenkaart">
                  <Button variant="outline" size="sm" className="ml-2">
                    <User className="h-4 w-4 mr-2" />
                    Mijn Account
                  </Button>
                </Link>
                <Button
                  variant="outline"
                  size="sm"
                  className="ml-2 border-[#e3d9c4] text-[#6b4912]"
                  onClick={handleLogout}
                >
                  Uitloggen
                </Button>
              </>
            ) : (
              <Link to="/login">
                <Button variant="outline" size="sm" className="ml-2">
                  <User className="h-4 w-4 mr-2" />
                  Login
                </Button>
              </Link>
            )}
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="md:hidden text-[#854C73] z-50"
            aria-label={isOpen ? 'Sluit menu' : 'Open menu'}
            aria-expanded={isOpen}
            aria-controls="mobile-menu"
          >
            {isOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <>
            {/* Backdrop */}
            <div 
              className="fixed inset-0 bg-black bg-opacity-50 z-[98] md:hidden"
              onClick={() => setIsOpen(false)}
            />
            
            {/* Menu Panel */}
            <div className="fixed inset-0 bg-[#FAEBEE] z-[99] flex flex-col items-center justify-center gap-6 animate-fade-in p-6 overflow-y-auto md:hidden">
              {/* Header */}
              <div className="flex items-center justify-between w-full max-w-xs mb-4">
                <OptimizedImage src={systemiqueLogo} webpSrc={systemiqueLogoWebp} alt="Systemique - Systemisch werk en coaching Castricum" className="h-10 w-auto object-contain" width={120} height={40} priority={true} />
                <button
                  onClick={() => setIsOpen(false)}
                  className="text-[#854C73]"
                >
                  <X size={28} />
                </button>
              </div>

              {/* Menu content */}
              <div className="w-full max-w-xs">
                <div className="w-full bg-white/50 rounded-xl p-4 text-center mb-4">
                  <button onClick={() => { setIsOpen(false); navigate('/'); }} className="block w-full py-2 text-[#6b4912]">Home</button>
                </div>
                <div className="w-full bg-white/50 rounded-xl p-4 text-center mb-4">
                  <button onClick={() => { setIsOpen(false); navigate('/over'); }} className="block w-full py-2 text-[#6b4912]">Over mij</button>
                </div>
                <div className="w-full bg-white/50 rounded-xl p-4 text-center mb-4">
                  <button onClick={() => { setIsOpen(false); navigate('/aanbod'); }} className="block w-full py-2 text-[#6b4912] font-bold mb-4">Aanbod</button>
                  {navigation.find(item => item.name === 'Aanbod')?.subItems?.map(item => (
                    <button 
                      key={item.name} 
                      onClick={() => { 
                        setIsOpen(false);
                        setMobileDropdown(null);
                        navigate(item.href);
                      }} 
                      className="block w-full py-2 text-[#6b4912]"
                    >
                      {item.name}
                    </button>
                  ))}
                </div>
                <div className="w-full bg-white/50 rounded-xl p-4 text-center mb-4">
                  <button onClick={() => { setIsOpen(false); navigate('/agenda'); }} className="block w-full py-2 text-[#6b4912] font-bold mb-4">Agenda</button>
                  {navigation.find(item => item.name === 'Agenda')?.subItems?.map(item => (
                    <button 
                      key={item.name} 
                      onClick={() => { 
                        setIsOpen(false);
                        setMobileDropdown(null);
                        navigate(item.href);
                      }} 
                      className="block w-full py-2 text-[#6b4912]"
                    >
                      {item.name}
                    </button>
                  ))}
                </div>
                <div className="w-full bg-white/50 rounded-xl p-4 text-center mb-4">
                  <button onClick={() => { setIsOpen(false); navigate('/contact'); }} className="block w-full py-2 text-[#6b4912]">Contact</button>
                </div>
                
                {/* User buttons */}
                {user && (
                  <div className="mt-6 pt-4 border-t border-white/20 w-full">
                    {isAdmin && (
                      <Link to="/admin" onClick={() => setIsOpen(false)} className="block mb-2">
                        <Button variant="outline" size="sm" className="w-full">
                          <Shield className="h-4 w-4 mr-2" />
                          Admin
                        </Button>
                      </Link>
                    )}
                    <Link to="/mijn-strippenkaart" onClick={() => setIsOpen(false)} className="block mb-2">
                      <Button variant="outline" size="sm" className="w-full">
                        <User className="h-4 w-4 mr-2" />
                        Mijn Account
                      </Button>
                    </Link>
                    <Button variant="outline" size="sm" className="w-full" onClick={handleLogout}>
                      Uitloggen
                    </Button>
                  </div>
                )}
                {!user && (
                  <Link to="/login" onClick={() => setIsOpen(false)} className="block mt-6">
                    <Button variant="outline" size="sm" className="w-full">
                      <User className="h-4 w-4 mr-2" />
                      Login
                    </Button>
                  </Link>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </nav>
  );
};

export default Navigation;